import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Card,
  CardInfo,
  CreateCardIdentifiers,
  CreateCardOptions,
  CreateCardResponse,
  ListCardsOptions,
  ListCardsResponse,
  MoveCardOptions,
  RestoreCardOptions,
  archiveCard as universalArchiveCard,
  createCard as universalCreateCard,
  deleteCard as universalDeleteCard,
  getCard as universalGetCard,
  listCards as universalListCards,
  moveCard as universalMoveCard,
  restoreCard as universalRestoreCard,
  updateCard as universalUpdateCard,
} from './workflows-v1-card-cards.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/workflows' };

export function listCards(httpClient: HttpClient): ListCardsSignature {
  return (workflowId: string, options?: ListCardsOptions) =>
    universalListCards(
      workflowId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCardsSignature {
  /**
   * Retrieves a list of a workflow's cards.
   *
   *
   * The `listCards()` function returns a Promise that resolves to a list of the specified workflow's cards.
   *
   *
   * Use the `options` parameter to specify which cards to retrieve and in which order to retrieve them. Must use either `phaseId` or `fetchOnlyArchived`.
   *
   *
   * Cards can be sorted based on their `"id"`, `"name"`, `"phaseId"`, `"createdDate"`, `"updatedDate"`, `"source"`, and `"position"`.
   *
   *
   * If no `limit` parameter is passed, the first 50 cards are returned.
   *
   *
   * Sort order defaults to by `"phaseId"` and then by `"position"` ascending.
   *
   *
   * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   * @param - ID of the workflow whose cards will be listed.
   * @param - Options to use when retrieving the list of cards.
   */
  (workflowId: string, options?: ListCardsOptions): Promise<
    NonNullablePaths<
      ListCardsResponse,
      | `cards`
      | `cards.${number}.info.primaryAttachment.attachmentId`
      | `cards.${number}.info.primaryAttachment.value`
      | `cards.${number}.info.primaryAttachment.attachmentType`
      | `cards.${number}.info.primaryAttachment.contactId`
      | `pagination.offset`
      | `pagination.total`
      | `pagination.count`,
      6
    >
  >;
}

export function getCard(httpClient: HttpClient): GetCardSignature {
  return (_id: string) =>
    universalGetCard(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCardSignature {
  /**
   * Retrieves a workflow card by ID.
   *
   *
   * The `getCard()` function returns a Promise that resolves to the card with the specified ID.
   *
   *
   * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   * @param - ID of the card to retrieve.
   * @returns Requested card.
   */
  (_id: string): Promise<
    NonNullablePaths<
      Card,
      | `info.primaryAttachment.attachmentId`
      | `info.primaryAttachment.value`
      | `info.primaryAttachment.attachmentType`
      | `info.primaryAttachment.contactId`,
      4
    >
  >;
}

export function updateCard(httpClient: HttpClient): UpdateCardSignature {
  return (_id: string, cardInfo: CardInfo) =>
    universalUpdateCard(
      _id,
      cardInfo,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCardSignature {
  /**
   * Updates an existing workflow card.
   *
   *
   * The `updateCard()` function returns a Promise that resolves when the card has been updated with the specified values.
   *
   *
   * `contactId` is not a required field, but if the parameter is not passed, the field will default to empty and the card will not be associated with any contact.
   *
   *
   * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   * @param - ID of the card to update.
   * @param - Card details.
   * @param - Options to use when updating a card.
   */
  (_id: string, cardInfo: CardInfo): Promise<void>;
}

export function createCard(httpClient: HttpClient): CreateCardSignature {
  return (
    identifiers: NonNullablePaths<
      CreateCardIdentifiers,
      `phaseId` | `workflowId`,
      2
    >,
    card: CardInfo,
    options?: CreateCardOptions
  ) =>
    universalCreateCard(
      identifiers,
      card,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCardSignature {
  /**
   * Creates a new workflow card.
   *
   *
   * The `createCard()` function returns a Promise that resolves to the created card's ID when the card is created in the specified workflow phase.
   *
   * Pass a value for the `position` parameter to specify the newly created card's position within the specified phase. Positions are zero-based, meaning the first position is `0`.
   *
   * When omitted, the newly created card is added to the specified phase as the top card within the phase.
   *
   *
   * This function requires you to specify the ID of a workflow and phase. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   *
   *  > **Note:**
   *  > Each workflow is limited to 5,000 cards.
   *  > If this limit is reached, `createCard()` throws an error.
   *  > [Archive cards](#archivecard) when they're no longer needed
   *  > to reduce card count and avoid hitting the limit.
   * @param - Card details.
   * @param - Options to use when creating a card.
   */
  (
    identifiers: NonNullablePaths<
      CreateCardIdentifiers,
      `phaseId` | `workflowId`,
      2
    >,
    card: CardInfo,
    options?: CreateCardOptions
  ): Promise<CreateCardResponse>;
}

export function deleteCard(httpClient: HttpClient): DeleteCardSignature {
  return (_id: string) =>
    universalDeleteCard(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCardSignature {
  /**
   * Deletes a workflow card by ID.
   *
   *
   * The `deleteCard()` function returns a Promise when the specified card has been deleted.
   *
   * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   * @param - ID of the card to delete.
   */
  (_id: string): Promise<void>;
}

export function moveCard(httpClient: HttpClient): MoveCardSignature {
  return (_id: string, options?: MoveCardOptions) =>
    universalMoveCard(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface MoveCardSignature {
  /**
   * Moves a card to a new position within a workflow.
   *
   *
   * The `moveCard()` function returns a Promise when the specified card has been moved to the new position.
   *
   * Use the `options` parameter to specify the phase and position within that phase to move the card to. Positions are zero-based, meaning the first position is `0`.
   *
   * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   * @param - ID of the card to move.
   * @param - Information about where to move the card.
   */
  (_id: string, options?: MoveCardOptions): Promise<void>;
}

export function restoreCard(httpClient: HttpClient): RestoreCardSignature {
  return (_id: string, newPhaseId: string, options?: RestoreCardOptions) =>
    universalRestoreCard(
      _id,
      newPhaseId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RestoreCardSignature {
  /**
   * Restores an archived workflow card.
   *
   *
   * The `restoreCard()` function returns a Promise that resolves when the archived card has been restored.
   *
   *
   * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   *
   *
   * > **Note:**
   * > Each workflow is limited to 5,000 cards.
   * > If this limit is reached, `createCard()` throws an error.
   * > [Archive cards](#archivecard) when they're no longer needed
   * > to reduce card count and avoid hitting the limit.
   * @param - ID of the card to restore.
   * @param - ID of the phase to restore the card to.
   * @param - Options to use when restoring a card.
   */
  (
    _id: string,
    newPhaseId: string,
    options?: RestoreCardOptions
  ): Promise<void>;
}

export function archiveCard(httpClient: HttpClient): ArchiveCardSignature {
  return (_id: string) =>
    universalArchiveCard(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface ArchiveCardSignature {
  /**
   * Archives a workflow card.
   *
   *
   * The `archiveCard()` function returns a Promise that resolves when the card has been archived.
   *
   *
   * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
   * @param - ID of the card to archive.
   */
  (_id: string): Promise<void>;
}

export {
  AccountInfo,
  ActionEvent,
  ArchiveCardRequest,
  ArchiveCardResponse,
  Attachment,
  AttachmentType,
  Card,
  CardInfo,
  CreateCardIdentifiers,
  CreateCardOptions,
  CreateCardRequest,
  CreateCardResponse,
  DeleteCardRequest,
  DeleteCardResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  GetCardRequest,
  GetCardResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListCardsOptions,
  ListCardsRequest,
  ListCardsResponse,
  MessageEnvelope,
  MoveCardOptions,
  MoveCardRequest,
  MoveCardResponse,
  PaginationResponse,
  Paging,
  Query,
  QueryCardsRequest,
  QueryCardsResponse,
  RestoreCardOptions,
  RestoreCardRequest,
  RestoreCardResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UpdateCardRequest,
  UpdateCardResponse,
  WebhookIdentityType,
} from './workflows-v1-card-cards.universal.js';
